import {
    AccountType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroupItemFLI,
    IBalanceSwitchAccount,
    IFinancialStatementInterpretation,
    Signs,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { DefaultGroupsNames } from '@modeso/types__aumico-lib-templates-be';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
const debugBsa = Debug('aumico:BalanceSwitchAccountService - bsa');

export interface SplittedAccount {
    originalAccount: IBalanceSwitchAccount;
    counterpartAccount: IBalanceSwitchAccount;
    balanceSwitchAccount: IBalanceSwitchAccount; //account with closing and opening balance with different signs, this will eventually be saved in the balanceSwitchAccounts array
}

export interface MergedAccount {
    originalAccount: IBalanceSwitchAccount;
    counterpartAccount: IBalanceSwitchAccount;
    sourcedBalanceSwitchAccount: IBalanceSwitchAccount; //account with closing and opening balance with different signs, this will eventually be saved in the balanceSwitchAccounts array
    newAccount: IAccount; //account with closing and opening balance with different signs, this will eventually be saved in the balanceSwitchAccounts array
}

export interface SwitchAccountMappingResults {
    splittedAccounts: SplittedAccount[];
    mergedAccounts: MergedAccount[];
}

export enum AccountLocation {
    ORIGINAL_ASSETS = 'ORIGINAL_ASSETS',
    ORIGINAL_LIABILITIES = 'ORIGINAL_LIABILITIES',
    COUNTERPART_ASSETS = 'COUNTERPART_ASSETS',
    COUNTERPART_LIABILITIES = 'COUNTERPART_LIABILITIES',
    REGULAR = 'REGULAR',
}

export interface SwitchPart {
    openingBalance: number; // raw signed numbers
    closingBalance: number;
    originalOpeningBalance: number; // raw signed number of FIRST year
    switchPartCategory?: AccountLocation;
    interpretation?: Signs;
}

export enum AccountOrigin {
    ASSETS = 'ASSETS',
    LIABILITIES = 'LIABILITIES',
}

export interface IExtendedAccount extends IAccount {
    ignoreFliSign?: boolean;
}

export type Side = 'asset' | 'liability' | 'unknown';
type SignConvention = 'positive' | 'negative';

export class BalanceSwitchAccountService {
    /**
     * Determines if an account qualifies as a balance switch account
     * by comparing if opening and closing balances have different signs
     */
    isBalanceSwitchCandidate(account: IAccount): boolean {
        return (
            account.calculatedClosingBalance * account.calculatedOpeningBalance < 0 &&
            account.type !== AccountType.INCOME &&
            account.type !== AccountType.UNCOVERED_SHORTFALL
        );
    }

    determineAccountOrigin(
        parentItem: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
        defaultGroupName: string,
    ): AccountOrigin {
        // First check if fliType property exists on the parentItem
        if (parentItem && 'fliType' in parentItem) {
            // If fliType exists, use it to determine the origin
            if (parentItem.fliType === FinancialLineItemType.ASSETS) {
                return AccountOrigin.ASSETS;
            } else {
                return AccountOrigin.LIABILITIES;
            }
        } else {
            // If fliType doesn't exist, compare defaultGroupName with default names
            if (defaultGroupName === DefaultGroupsNames.ASSETS) {
                return AccountOrigin.ASSETS;
            } else {
                return AccountOrigin.LIABILITIES;
            }
        }
    }

    /**
     * Creates a pair of accounts (original and counterpart) from a single account
     * for proper representation in both assets and liabilities
     * @param sheetSystemRules The financial statement interpretation rules
     * @param account The account to split
     * @param origin The origin type (assets or liabilities)
     * @param latestAccountUpdates Optional array of latest account updates
     * @returns A SplittedAccount object containing the original, counterpart, and balance switch accounts
     */
    splitAccount(
        sheetSystemRules: IFinancialStatementInterpretation,
        account: IAccount | IBalanceSwitchAccount,
        origin?: AccountOrigin | undefined,
        latestAccountUpdates?: IAccount[] | undefined,
    ): SplittedAccount {
        // Validate required parameters
        if (origin === undefined) {
            throw new Error(
                `Origin is required to determine if account ${account.accountNumber} is in assets or liabilities`,
            );
        }

        // Update account with latest values if available
        if (latestAccountUpdates) {
            //this.updateAccountWithLatestValues(account, latestAccountUpdates);

            const matchingAccounts = latestAccountUpdates.filter((acc) => acc.accountNumber === account.accountNumber);
            if (matchingAccounts.length === 1) {
                // In this case the saved account is not split yet
                debugBsa(
                    `Account ${account.accountNumber} is not split yet, updating with latest values.`,
                    matchingAccounts,
                );
                this.updateAccountWithLatestValues(account, latestAccountUpdates);
            } else {
                // In this case the saved accounts are already split
                debugBsa(
                    `Account ${account.accountNumber} is already split, merging balances from ${matchingAccounts.length} accounts.`,
                    matchingAccounts,
                );
                const onlyBalanceSwitchAccounts = matchingAccounts.filter((acc) => acc.isBalanceSwitchAccount === true);
                let originalAccount: IBalanceSwitchAccount | undefined;
                let counterpartAccount: IBalanceSwitchAccount | undefined;
                let balanceSwitchAccount: IBalanceSwitchAccount | undefined;

                onlyBalanceSwitchAccounts.forEach((acc: IBalanceSwitchAccount) => {
                    if (acc.originsFromAsset) {
                        originalAccount = cloneDeep(acc) as IBalanceSwitchAccount;
                    } else {
                        counterpartAccount = cloneDeep(acc) as IBalanceSwitchAccount;
                    }
                });
                balanceSwitchAccount = cloneDeep(onlyBalanceSwitchAccounts[0]) as IBalanceSwitchAccount;

                // BUGFIX (double-save sign flip): the previous implementation rebuilt the combined
                // opening/closing by naively summing the two legs' FLI-signed balances. When both
                // sides share the same sign convention (e.g. assets & liabilities both POSITIVE),
                // that sum LOSES the original sign — a closing of -1'000 comes back as +1'000 on the
                // second save, which then corrupts unmapping and next-year autofill.
                //
                // The two legs are stored as positive magnitudes on their own side. `originalAccount`
                // is the asset-side leg (originsFromAsset === true), `counterpartAccount` the
                // liability-side leg (originsFromAsset === false). The true signed economic value
                // orients by the account's ORIGIN:
                //   - asset-origin  (a bank): merged = asset side − liability side.
                //   - liability-origin (a credit card): the legs are MIRRORED → merged = liability − asset.
                // BUGFIX (REV-21304, Bug A): the previous code used (asset − liability) unconditionally,
                // which flipped the signs of a liability-origin switch account on every load (its
                // originsFromAsset was also silently taken from the arbitrary first leg). Honour the
                // caller-provided `origin` for both the combined value AND the summary's origin flag.
                const originIsAsset = origin === AccountOrigin.ASSETS;
                balanceSwitchAccount.originsFromAsset = originIsAsset;
                const assetOpen = this.getLegMagnitude(originalAccount, 'opening');
                const assetClose = this.getLegMagnitude(originalAccount, 'closing');
                const liabOpen = this.getLegMagnitude(counterpartAccount, 'opening');
                const liabClose = this.getLegMagnitude(counterpartAccount, 'closing');
                const combinedCalculatedOpeningBalance = originIsAsset ? assetOpen - liabOpen : liabOpen - assetOpen;
                const combinedCalculatedClosingBalance = originIsAsset ? assetClose - liabClose : liabClose - assetClose;

                balanceSwitchAccount.calculatedOpeningBalance = combinedCalculatedOpeningBalance;
                balanceSwitchAccount.calculatedClosingBalance = combinedCalculatedClosingBalance;

                // Raw balances follow the BSA's own interpretation (a NEGATIVE-signed FLI stores the
                // inverse of the economic value), keeping raw and calculated consistent & sign-correct.
                if ((balanceSwitchAccount as IBalanceSwitchAccount).interpretation === Signs.NEGATIVE) {
                    balanceSwitchAccount.openingBalance = -1 * combinedCalculatedOpeningBalance + 0;
                    balanceSwitchAccount.closingBalance = -1 * combinedCalculatedClosingBalance + 0;
                } else {
                    balanceSwitchAccount.openingBalance = combinedCalculatedOpeningBalance;
                    balanceSwitchAccount.closingBalance = combinedCalculatedClosingBalance;
                }

                balanceSwitchAccount.balanceSwitchAccountId = account.accountNumber;
                // Set original balance values
                this.setOriginalBalanceValues(account, balanceSwitchAccount);

                return {
                    originalAccount,
                    counterpartAccount,
                    balanceSwitchAccount,
                };
            }
        }

        // Create clones of the account for different purposes
        const originalAccount: IBalanceSwitchAccount = cloneDeep(account);
        const counterpartAccount: IBalanceSwitchAccount = cloneDeep(account);
        const balanceSwitchAccount: IBalanceSwitchAccount = cloneDeep(account);

        // Set common properties for balance switch account
        balanceSwitchAccount.balanceSwitchAccountId = account.accountNumber;
        balanceSwitchAccount.originsFromAsset = origin === AccountOrigin.ASSETS;

        // Set original balance values
        this.setOriginalBalanceValues(account, balanceSwitchAccount);

        // Mark all accounts as balance switch accounts
        originalAccount.isBalanceSwitchAccount = true;
        counterpartAccount.isBalanceSwitchAccount = true;
        balanceSwitchAccount.isBalanceSwitchAccount = true;
        if (origin === AccountOrigin.ASSETS) {
            balanceSwitchAccount.interpretation = sheetSystemRules.assets;
            originalAccount.interpretation = sheetSystemRules.assets;
            counterpartAccount.interpretation = sheetSystemRules.liabilities;
        } else {
            balanceSwitchAccount.interpretation = sheetSystemRules.liabilities;
            originalAccount.interpretation = sheetSystemRules.liabilities;
            counterpartAccount.interpretation = sheetSystemRules.assets;
        }

        // Set origin properties
        if (origin === AccountOrigin.ASSETS) {
            originalAccount.originsFromAsset = true;
            counterpartAccount.originsFromAsset = false;
        } else {
            originalAccount.originsFromAsset = false;
            counterpartAccount.originsFromAsset = true;
        }

        // Distribute balances between original and counterpart accounts
        this.distributeBalances(originalAccount, counterpartAccount, account, origin);

        return {
            originalAccount,
            counterpartAccount,
            balanceSwitchAccount,
        };
    }

    /**
     * Updates account with latest values if available
     * @param account The account to update
     * @param latestAccountUpdates Optional array of latest account updates
     */
    private updateAccountWithLatestValues(
        account: IAccount | IBalanceSwitchAccount,
        latestAccountUpdates?: IAccount[],
    ): void {
        if (!latestAccountUpdates) return;

        const matchingAccounts = latestAccountUpdates.filter((acc) => acc.accountNumber === account.accountNumber);

        if (matchingAccounts.length > 0) {
            account.openingBalance = matchingAccounts.reduce((sum, acc) => sum + acc.openingBalance, 0);
            account.closingBalance = matchingAccounts.reduce((sum, acc) => sum + acc.closingBalance, 0);
            if ((account as IBalanceSwitchAccount).interpretation === Signs.NEGATIVE) {
                account.calculatedOpeningBalance = -1 * account.openingBalance + 0;
                account.calculatedClosingBalance = -1 * account.closingBalance + 0;
            } else {
                account.calculatedOpeningBalance = account.openingBalance;
                account.calculatedClosingBalance = account.closingBalance;
            }
            if (matchingAccounts.length > 0) {
                account.summarizedFrom = matchingAccounts[0].summarizedFrom;
            }
        }
    }

    /**
     * Sets original balance values for balance switch account
     * @param sourceAccount The source account
     * @param targetAccount The target balance switch account
     */
    private setOriginalBalanceValues(
        sourceAccount: IAccount | IBalanceSwitchAccount,
        targetAccount: IBalanceSwitchAccount,
    ): void {
        if ('originalClosingBalance' in sourceAccount && 'originalOpeningBalance' in sourceAccount) {
            // The account implements the IBalanceSwitchAccount interface
            targetAccount.originalClosingBalance = sourceAccount.originalClosingBalance;
            targetAccount.originalOpeningBalance = sourceAccount.originalOpeningBalance;
        } else {
            targetAccount.originalClosingBalance = sourceAccount.closingBalance;
            targetAccount.originalOpeningBalance = sourceAccount.openingBalance;
        }
    }

    /**
     * Returns the positive magnitude a split leg holds for the given period. Legs store their amount
     * as a positive magnitude on their own side, so the calculated balance's magnitude is authoritative;
     * we fall back to the raw balance's magnitude for legacy data that has no calculated value.
     */
    private getLegMagnitude(leg: IBalanceSwitchAccount | undefined, period: 'opening' | 'closing'): number {
        if (leg == null) {
            return 0;
        }
        const calculated = period === 'opening' ? leg.calculatedOpeningBalance : leg.calculatedClosingBalance;
        if (calculated != null && Number.isFinite(calculated)) {
            return Math.abs(calculated);
        }
        const raw = period === 'opening' ? leg.openingBalance : leg.closingBalance;
        return Math.abs(raw ?? 0);
    }

    /**
     * Recovers the trustworthy, sign-correct raw opening/closing of a balance switch account, used to
     * heal reports whose `closingBalance` sign was already corrupted by the double-save bug.
     *
     * Priority (per REV-21304):
     *   1. `originalClosingBalance` / `originalOpeningBalance` — the raw signed values captured at split
     *      time; these are preserved untouched by the bug, so they are trusted first when present.
     *   2. `calculatedClosingBalance` / `calculatedOpeningBalance` with the interpretation (import
     *      options) sign reversed — recovering the raw value the FLI sign was applied to.
     *   3. the (possibly corrupted) `closingBalance` / `openingBalance` as a last resort.
     */
    public static getReliableRawBalances(account: IBalanceSwitchAccount): { openingBalance: number; closingBalance: number } {
        const isNegative = account.interpretation === Signs.NEGATIVE;

        const resolve = (
            original: number | undefined,
            calculated: number | undefined,
            raw: number | undefined,
        ): number => {
            if (original != null && Number.isFinite(original)) {
                return original;
            }
            if (calculated != null && Number.isFinite(calculated)) {
                // Reverse-apply the import-options (interpretation) sign to recover the raw value.
                return isNegative ? -1 * calculated : calculated;
            }
            return raw ?? 0;
        };

        return {
            openingBalance: resolve(account.originalOpeningBalance, account.calculatedOpeningBalance, account.openingBalance),
            closingBalance: resolve(account.originalClosingBalance, account.calculatedClosingBalance, account.closingBalance),
        };
    }

    /**
     * REV-21304 (3rd-year carry bug): reconstruct a switch account's TRUE signed raw opening/closing
     * from its two split LEGS, which are the per-year ground truth. This is used when carrying a
     * previous year's switch account into the next year's opening (Vorjahr).
     *
     * `getReliableRawBalances` trusts `originalClosingBalance` first — correct for the SAME-YEAR
     * double-save heal (there the original is this report's own untouched snapshot). But `original*`
     * is captured at the FIRST split and then propagated UNCHANGED across years by
     * `setOriginalBalanceValues`, so from the SECOND switched year on it is STALE: a year-3 carry read
     * the frozen year-1 closing instead of the year-2 closing. The legs, by contrast, always hold the
     * current year's magnitudes (they are re-derived every save), so combining them the same way
     * `splitAccount` does yields the correct signed value — immune to BOTH the stale `original*` and
     * the double-save sign corruption of the summary's own `calculated`/`closing`.
     *
     * Returns `null` when the legs cannot be located, so callers fall back to `getReliableRawBalances`.
     */
    public static getSignedBalancesFromLegs(
        summary: IBalanceSwitchAccount,
        accountsPool: ReadonlyArray<IAccount | IBalanceSwitchAccount> | undefined,
    ): { openingBalance: number; closingBalance: number } | null {
        if (summary == null || !Array.isArray(accountsPool)) {
            return null;
        }
        const legs = accountsPool.filter(
            (a) =>
                a?.accountNumber === summary.accountNumber &&
                (a as IBalanceSwitchAccount).isBalanceSwitchAccount === true,
        ) as IBalanceSwitchAccount[];
        const assetLeg = legs.find((l) => l.originsFromAsset === true);
        const liabLeg = legs.find((l) => l.originsFromAsset === false);
        // Need at least one identifiable leg to reconstruct; otherwise let the caller fall back.
        if (assetLeg == null && liabLeg == null) {
            return null;
        }
        // Legs store positive magnitudes on their own side (mirrors splitAccount's leg-combining):
        // an asset-origin account nets asset − liability; a liability-origin one nets liability − asset.
        const mag = (leg: IBalanceSwitchAccount | undefined, period: 'opening' | 'closing'): number => {
            if (leg == null) {
                return 0;
            }
            const calculated = period === 'opening' ? leg.calculatedOpeningBalance : leg.calculatedClosingBalance;
            if (calculated != null && Number.isFinite(calculated)) {
                return Math.abs(calculated);
            }
            const raw = period === 'opening' ? leg.openingBalance : leg.closingBalance;
            return Math.abs(raw ?? 0);
        };
        const originIsAsset = summary.originsFromAsset === true;
        const combinedOpening = originIsAsset
            ? mag(assetLeg, 'opening') - mag(liabLeg, 'opening')
            : mag(liabLeg, 'opening') - mag(assetLeg, 'opening');
        const combinedClosing = originIsAsset
            ? mag(assetLeg, 'closing') - mag(liabLeg, 'closing')
            : mag(liabLeg, 'closing') - mag(assetLeg, 'closing');
        // The combined value is the economic (calculated) balance; reverse the interpretation to get
        // the raw signed value the carry expects (consistent with getReliableRawBalances' calculated branch).
        const isNegative = summary.interpretation === Signs.NEGATIVE;
        return {
            openingBalance: isNegative ? -1 * combinedOpening : combinedOpening,
            closingBalance: isNegative ? -1 * combinedClosing : combinedClosing,
        };
    }

    /**
     * Distributes balances between original and counterpart accounts
     * @param originalAccount The original account
     * @param counterpartAccount The counterpart account
     * @param sourceAccount The source account with original values
     */
    private distributeBalances(
        originalAccount: IBalanceSwitchAccount,
        counterpartAccount: IBalanceSwitchAccount,
        sourceAccount: IAccount | IBalanceSwitchAccount,
        origin: AccountOrigin,
    ): void {
        //: if calculated balance is 0 or positive, then the value is added to the original account, if negative, then it is added to the counterpart account
        if (sourceAccount.calculatedOpeningBalance >= 0) {
            originalAccount.calculatedOpeningBalance = sourceAccount.calculatedOpeningBalance;

            counterpartAccount.calculatedOpeningBalance = 0;
            counterpartAccount.openingBalance = 0;
        } else {
            counterpartAccount.calculatedOpeningBalance = Math.abs(sourceAccount.calculatedOpeningBalance);

            originalAccount.calculatedOpeningBalance = 0;
            originalAccount.openingBalance = 0;
        }
        if (sourceAccount.calculatedClosingBalance >= 0) {
            originalAccount.calculatedClosingBalance = sourceAccount.calculatedClosingBalance;

            counterpartAccount.calculatedClosingBalance = 0;
            counterpartAccount.closingBalance = 0;
        } else {
            counterpartAccount.calculatedClosingBalance = Math.abs(sourceAccount.calculatedClosingBalance);

            originalAccount.calculatedClosingBalance = 0;
            originalAccount.closingBalance = 0;
        }

        if (originalAccount.interpretation === Signs.NEGATIVE) {
            originalAccount.openingBalance = -1 * originalAccount.calculatedOpeningBalance + 0;
            originalAccount.closingBalance = -1 * originalAccount.calculatedClosingBalance + 0;
        } else {
            originalAccount.openingBalance = originalAccount.calculatedOpeningBalance;
            originalAccount.closingBalance = originalAccount.calculatedClosingBalance;
        }

        if (counterpartAccount.interpretation === Signs.NEGATIVE) {
            counterpartAccount.openingBalance = -1 * counterpartAccount.calculatedOpeningBalance + 0;
            counterpartAccount.closingBalance = -1 * counterpartAccount.calculatedClosingBalance + 0;
        } else {
            counterpartAccount.openingBalance = counterpartAccount.calculatedOpeningBalance;
            counterpartAccount.closingBalance = counterpartAccount.calculatedClosingBalance;
        }
    }


    /**
     * Repairs an "Old Format" balance switch account by hydrating it with 
     * fresh values and missing metadata.
     */
    public static repairOldSwitchAccount(
        freshData: IAccount,
        oldAssetAccount: IAccount | undefined,
        oldLiabilityAccount: IAccount | undefined,
        assetSign: Signs = Signs.POSITIVE,
        liabilitySign: Signs = Signs.NEGATIVE
    ): IBalanceSwitchAccount {
        // 1. DETERMINE ORIGIN
        // If the account existed in Assets previously (has rowNo etc), it originates there.
        // Default to TRUE (Assets) if we can't tell, as most BSAs are bank accounts.
        const originIsAssets = !!oldAssetAccount;

        // 2. DETERMINE INTERPRETATION
        const interpretation = originIsAssets ? assetSign : liabilitySign;

        // 3. CONSTRUCT THE REPAIRED CANDIDATE
        // We take the "Fresh" data (current year values) and add the missing metadata.
        const repairedCandidate: IBalanceSwitchAccount = {
            ...freshData, // Contains correct name, accountNumber, and current year balances

            // --- RESTORE IDENTITY ---
            balanceSwitchAccountId: freshData.accountNumber,
            isBalanceSwitchAccount: true,
            originsFromAsset: originIsAssets,
            interpretation: interpretation,
            type: AccountType.BALANCE,

            // --- RESTORE ORIGINAL VALUES ---
            // The service needs these to know what the "Unsplit" total was
            originalOpeningBalance: freshData.openingBalance || 0,
            originalClosingBalance: freshData.closingBalance || 0,

            // --- ENSURE CALCULATED VALUES EXIST ---
            // If freshData doesn't have them, calculate them simply based on interpretation
            calculatedOpeningBalance: freshData.calculatedOpeningBalance ??
                (interpretation === 'negative' ? -1 * (freshData.openingBalance || 0) : (freshData.openingBalance || 0)),

            calculatedClosingBalance: freshData.calculatedClosingBalance ??
                (interpretation === 'negative' ? -1 * (freshData.closingBalance || 0) : (freshData.closingBalance || 0)),
        };

        return repairedCandidate;
    }
}